import { createContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_TRACE_LIMIT,
  getMaxYFromBar,
  LEGEND_ONLY,
  TOP_SORT_DIRECTION,
} from '../../../../components/Graphs/constants';
import {
  BAR_GRAPH_AGGREGATES,
  BAR_GRAPH_TYPES,
  COUNTRY_AGGREGATE,
  CURRENT_METRIC_SORT_TYPE,
  graphTitles,
  isGraphTypeEmission,
  REMAINING_EMISSIONS_INTENSITY,
  SCOPE_1_AND_2_SOURCE_TYPES,
  SORT_TYPES,
} from '../utils';

import { useQuery } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { downloadXlsx } from '../../../../components/Downloads/helpers';
import GraphControls from '../../../../components/Graphs/GraphControls';
import { GHG_GOOGLE_ANALYTICS_TAG } from '../../constants';
import { GRAPH_ASSETS_QUERY_NEW } from '../operations';

const BarGraphContextLeft = createContext();
const visibleTraces = {};

export const BarGraphProviderLeft = ({ children, portfolios }) => {
  const [openControlsModal, setOpenControlsModal] = useState(false);
  const [aggregate, setAggregate] = useState(COUNTRY_AGGREGATE.name);
  const [graphType, setGraphType] = useState(REMAINING_EMISSIONS_INTENSITY);
  const [emissionsSource, setEmissionsSource] = useState([...SCOPE_1_AND_2_SOURCE_TYPES]);
  const [limit, setLimit] = useState(DEFAULT_TRACE_LIMIT);
  const [sortDirection, setSortDirection] = useState(TOP_SORT_DIRECTION);
  const [sortMetric, setSortMetric] = useState(CURRENT_METRIC_SORT_TYPE);
  const [range, setRange] = useState({ x: [], y: [] });
  const [modalRange, setModalRange] = useState({ x: [], y: [] });
  const [showPlotlyLegend, setShowPlotlyLegend] = useState(true);

  const resetRange = () => {
    setRange({ x: [], y: [] });
    setModalRange({ x: [], y: [] });
  };

  const onRelayout = (e) => {
    const x = [e['xaxis.range[0]'], e['xaxis.range[1]']];
    const y = [e['yaxis.range[0]'], e['yaxis.range[1]']];
    setRange({ x, y });
  };

  const onGraphUpdate = () => {
    if (
      (range.x[0] !== modalRange.x[0] && range.x[1] !== modalRange.x[1]) ||
      (range.y[0] !== modalRange.y[0] && range.y[1] !== modalRange.y[1])
    ) {
      setModalRange(range);
    }
  };

  const { data, loading } = useQuery(GRAPH_ASSETS_QUERY_NEW, {
    variables: {
      aggregates: [aggregate],
      chartType: 'bar',
      graphType: graphType.value,
      emissionsSource,
      portfolios,
      limit: parseInt(limit),
      sortDirection: sortDirection.value,
      sortMetric: sortMetric.value,
      visibleTraces,
    },
  });

  const trace = useMemo(() => {
    let analytics = data?.graphAssetsNew?.analytics || [];
    // Workaround for bug where the chart will not display if the marker color is set:
    return cloneDeep(analytics);
  }, [data]);

  const onLegendClick = (data) => {
    // get the index of the trace that was clicked
    const clickedIdx = data.curveNumber;
    const { visible } = data.fullData[clickedIdx];
    const clickedTrace = trace[clickedIdx];
    visibleTraces[clickedTrace.name] = visible === true ? LEGEND_ONLY : true;
    clickedTrace.visible = visibleTraces[clickedTrace.name];
    trace[clickedIdx] = clickedTrace;
    const maxY = getMaxYFromBar(trace);
    setRange({ x: range.x, y: [0, maxY] });
    setModalRange({ x: range.x, y: [0, maxY] });
  };

  const onLegendDoubleClick = (data) => {
    // get the index of the trace that was double clicked
    const clickedIdx = data.curveNumber;
    // if the trace that was double clicked is not visible then we want to show all
    // if the trace that was double clicked is visible then
    // a: hide all others if any others are visible
    // b: show all if all others are hidden
    const { visible } = data.fullData[clickedIdx];
    const hideAll =
      visible === true &&
      !!data.fullData.find((t, idx) => clickedIdx !== idx && t.visible === true);
    // toggle the traces
    trace.forEach((t, idx) => {
      const { name } = t;
      if (hideAll && clickedIdx !== idx) {
        visibleTraces[name] = LEGEND_ONLY;
      } else {
        visibleTraces[name] = true;
      }
    });
    if (hideAll) {
      setRange({ x: range.x, y: [0, Math.max(...trace[clickedIdx].y)] });
      setModalRange({ x: range.x, y: [0, Math.max(...trace[clickedIdx].y)] });
    } else {
      resetRange();
    }
  };

  useEffect(() => {
    resetRange();
  }, [data]);

  const downloadCallback = (errorHandler, successHandler) => {
    const path = 'portfolio-analysis/export-asset-bar-graph-data/';
    const body = {
      aggregate,
      graphType: graphType.value,
      emissionsSource,
      portfolios,
      limit,
      sortDirection: sortDirection.value,
      sortMetric: sortMetric.value,
      visibleTraces,
    };
    const downloadName = `${portfolios[0].name}_Asset_Data_by_${aggregate}.xlsx`;

    downloadXlsx(path, body, downloadName, errorHandler, successHandler);
  };

  return (
    <BarGraphContextLeft.Provider
      value={{
        downloadCallback,
        graphType,
        emissionsSource,
        onLegendClick,
        loading,
        modalRange,
        onGraphUpdate,
        onLegendDoubleClick,
        onRelayout,
        range,
        setModalRange,
        setOpenControlsModal,
        setShowPlotlyLegend,
        showPlotlyLegendToggle: emissionsSource.length > 1 && isGraphTypeEmission(graphType),
        showPlotlyLegend,
        sortDirection,
        sortMetric,
        trace,
        maxY: data?.graphAssetsNew?.maxY || [],
        googleAnalyticsTag: GHG_GOOGLE_ANALYTICS_TAG,
        graphTitles,
      }}
    >
      {children}
      <GraphControls
        aggregate={aggregate}
        aggregateTypes={BAR_GRAPH_AGGREGATES}
        googleAnalyticsTag={GHG_GOOGLE_ANALYTICS_TAG}
        graphType={graphType}
        emissionsSource={emissionsSource}
        includeOptions
        includeSortLimit
        limit={limit}
        open={openControlsModal}
        setAggregate={setAggregate}
        setGraphType={setGraphType}
        setEmissionsSource={setEmissionsSource}
        setLimit={setLimit}
        setOpen={setOpenControlsModal}
        setSortDirection={setSortDirection}
        setSortMetric={setSortMetric}
        sortDirection={sortDirection}
        sortMetric={sortMetric}
        sortTypes={SORT_TYPES}
        types={BAR_GRAPH_TYPES}
      />
    </BarGraphContextLeft.Provider>
  );
};

export const BarGraphConsumerLeft = BarGraphContextLeft.Consumer;

export default BarGraphContextLeft;
